// pages/HistoryView.tsx
import React, { useEffect, useMemo, useState } from "react";
import "./historial.css";
import {
  Table,
  Tag,
  Space,
  Input,
  DatePicker,
  Select,
  Typography,
  Tooltip,
  Button,
  Modal,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { Dayjs } from "dayjs";
import { formatDate } from "../../utils/formatDate";
import { useHistorialLogic } from "./useHistorialLogic";
import {
  CADUCIDAD_FECHAS_DIAS,
  deserializarRango,
  serializarRango,
} from "../../hooks/filtrosPersistidos";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import { apiFetch } from "../../services/apiClient";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";

const { RangePicker } = DatePicker;
const { Paragraph, Text } = Typography;

type RecordRow = {
  id: string;
  accion:
    | "create"
    | "update"
    | "movimiento"
    | "hide"
    | "unhide"
    | "undo"
    | string;
  quien_realiza: string;
  informacion: string;
  resumen: string;
  created_at: string;
  undone_at?: string | null;
  reversible: boolean;
  json_data: any;
};

// Claves = valores REALES de la columna `historial.accion` (lo que graba el
// backend: crear_producto / actualizar_producto / movimiento / ocultar / undo).
// Antes usaban kinds en inglés (create/update/hide/unhide) que no existen en
// esa columna → ni el color ni los filtros matcheaban (auditoría 2026-07-13).
const accionColor: Record<string, string> = {
  crear_producto: "green",
  actualizar_producto: "blue",
  movimiento: "purple",
  ocultar: "orange",
  undo: "default",
  // Round 2026-07-26 — ambas son irreversibles (`reversible: false`).
  compra_fusionada: "cyan",
  editar_transaccion: "gold",
  eliminar_transaccion: "red",
  mostrar: "green",
  // Round 2026-08-06 — borrados con revisión previa obligatoria.
  eliminar_propiedad: "volcano",
  eliminar_flujo_huerfano: "magenta",
};
const ACCION_LABELS: Record<string, string> = {
  crear_producto: "Crear producto",
  actualizar_producto: "Actualizar producto",
  movimiento: "Movimiento",
  ocultar: "Ocultar",
  undo: "Deshacer (undo)",
  compra_fusionada: "Compra fusionada",
  editar_transaccion: "Corregir transacción",
  eliminar_transaccion: "Eliminar transacción",
  mostrar: "Volver a mostrar",
  eliminar_propiedad: "Eliminar propiedad",
  eliminar_flujo_huerfano: "Eliminar flujo sin transacción",
};

// Opciones del Select "Acción". Values = valores reales de historial.accion (el
// backend filtra por igualdad exacta). Antes 4 de 6 opciones no existían en la
// DB y devolvían siempre 0 resultados (auditoría 2026-07-13). Vive acá arriba
// porque además es la lista blanca del filtro recordado: una acción retirada
// del catálogo no puede volver del storage y dejar la tabla en cero.
const OPCIONES_ACCION = [
  { value: "crear_producto", label: "Crear producto" },
  { value: "actualizar_producto", label: "Actualizar producto" },
  { value: "movimiento", label: "Movimiento" },
  { value: "ocultar", label: "Ocultar" },
  { value: "undo", label: "Deshacer (undo)" },
  { value: "compra_fusionada", label: "Compra fusionada" },
  { value: "editar_transaccion", label: "Corregir transacción" },
  { value: "eliminar_transaccion", label: "Eliminar transacción" },
  { value: "mostrar", label: "Volver a mostrar" },
  { value: "eliminar_propiedad", label: "Eliminar propiedad" },
  {
    value: "eliminar_flujo_huerfano",
    label: "Eliminar flujo sin transacción",
  },
];

const affectedIds = (r: RecordRow): number[] => {
  const jd = r.json_data || {};
  try {
    switch (jd.kind) {
      case "create":
      case "update":
      case "hide":
      case "unhide":
        return jd.itemId ? [Number(jd.itemId)] : [];
      case "movement":
        return [
          jd?.origin?.before?.id,
          jd?.origin?.after?.id,
          jd?.destination?.before?.id,
          jd?.destination?.after?.id,
          jd?.move?.productId,
          jd?.move?.destinoProductId,
        ]
          .filter((x: any) => Number.isFinite(Number(x)))
          .map((x: any) => Number(x));
      default:
        return [];
    }
  } catch {
    return [];
  }
};


const Historial: React.FC = () => {
  const [data, setData] = useState<RecordRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [total, setTotal] = useState(0);

  // filtros UI — se recuerdan por usuario admin en este navegador
  // (round 2026-09-24, ver docs/filtros-persistentes.md).
  const [q, setQ] = useFiltroPersistido<string | undefined>(
    "historial.busqueda",
    undefined,
    { validar: (v) => v === undefined || typeof v === "string" },
  );
  const [accion, setAccion] = useFiltroPersistido<string | undefined>(
    "historial.accion",
    undefined,
    {
      validar: (v) =>
        v === undefined || OPCIONES_ACCION.some((o) => o.value === v),
    },
  );
  const [undone, setUndone] = useState<string>(); // "true"|"false"|undefined
  const [reversible, setReversible] = useState<string>();
  const [range, setRange] = useFiltroPersistido<[Dayjs, Dayjs] | null>(
    "historial.rango",
    null,
    {
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      serializar: serializarRango,
      deserializar: (crudo) => {
        const r = deserializarRango(crudo);
        if (r === undefined) return undefined;
        // El filtro necesita las dos puntas: media fecha no filtra nada.
        return r && r[0] && r[1] ? ([r[0], r[1]] as [Dayjs, Dayjs]) : null;
      },
    },
  );
  // Texto visible del buscador. NO se persiste: es un espejo de `q`, que es el
  // término realmente aplicado (la caja filtra recién con Enter o la lupa).
  const [textoBusqueda, setTextoBusqueda] = useState<string>(() => q ?? "");
  useEffect(() => {
    setTextoBusqueda(q ?? "");
  }, [q]);
  const [jsonViewer, setJsonViewer] = useState<{
    open: boolean;
    title: string;
    payload: any | null;
  }>({ open: false, title: "", payload: null });

  const openJson = (title: string, payload: any) =>
    setJsonViewer({ open: true, title, payload });

  const closeJson = () => setJsonViewer((v) => ({ ...v, open: false }));

  const jsonText = useMemo(
    () =>
      jsonViewer.payload ? JSON.stringify(jsonViewer.payload, null, 2) : "",
    [jsonViewer.payload],
  );

  const { undoButton } = useHistorialLogic(() => fetchData(page, size));
  const { sidebarOpen, toggleSidebar } = useHistorialLogic();
  const fetchData = async (p = 1, s = size) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("page", String(p));
      params.set("size", String(s));
      if (q) params.set("q", q);
      if (accion) params.set("accion", accion);
      if (undone) params.set("undone", undone);
      if (reversible) params.set("reversible", reversible);
      if (range) {
        params.set("from", range[0].toISOString());
        params.set("to", range[1].toISOString());
      }
      const json = await apiFetch<any>(
        `/historial?${params.toString()}`,
      );
      if (!json) return;
      setData(json.data || []);
      setTotal(json.total || 0);
      setPage(json.page || 1);
      setSize(json.size || 20);
    } catch (e: any) {
      console.error(e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, size);
  }, [q, accion, undone, reversible, range]);

  const columns: ColumnsType<RecordRow> = useMemo(
    () => [
      {
        title: "Fecha",
        dataIndex: "created_at",
        width: 170,
        sorter: (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        render: (v) => formatDate(v),
        defaultSortOrder: "descend",
      },
      {
        title: "Acción",
        dataIndex: "accion",
        width: 150,
        // Sin `filters`/`onFilter` de columna: era client-side sobre la página
        // actual (la paginación es server-side) y con valores que no existen
        // en los datos — redundante con el Select "Acción" del header, que
        // filtra server-side el dataset completo (auditoría 2026-07-13).
        render: (_, r) => (
          <Tag color={accionColor[r.accion] || "default"}>
            {ACCION_LABELS[r.accion] || r.accion}
          </Tag>
        ),
      },
      {
        title: "Quién",
        dataIndex: "quien_realiza",
        width: 180,
        ellipsis: true,
      },
      {
        title: "Resumen",
        dataIndex: "resumen",
        width: 220,
        ellipsis: true,
      },
      {
        title: "Detalle",
        dataIndex: "informacion",
        ellipsis: { showTitle: false },
        render: (txt) => (
          <Tooltip title={txt}>
            <Text style={{ maxWidth: 360 }} ellipsis>
              {txt}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: "Afecta",
        key: "afecta",
        width: 160,
        render: (_, r) => {
          const ids = Array.from(new Set(affectedIds(r)));
          return ids.length ? (
            <Space wrap>
              {ids.map((id) => (
                <Tag key={id}>#{id}</Tag>
              ))}
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          );
        },
      },
      {
        title: "Estado",
        key: "estado",
        width: 150,
        render: (_, r) =>
          r.undone_at ? (
            <Tag color="default">Deshecha</Tag>
          ) : r.reversible ? (
            <Tag color="processing">Reversible</Tag>
          ) : (
            <Tag color="default">No reversible</Tag>
          ),
      },
      {
        title: "Acciones",
        key: "actions",
        width: 200,
        fixed: "right",
        render: (_, r) => (
          <Space>
            <Button
              size="small"
              type="link"
              onClick={() =>
                openJson(`Historial #${r.id} — ${r.accion}`, r.json_data)
              }
            >
              Ver JSON
            </Button>
            {!r.undone_at && r.reversible ? undoButton(String(r.id)) : null}
          </Space>
        ),
      },
    ],
    [undoButton],
  );

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="historial-page"
    >
      <div style={{ margin: "0 24px" }}>
        <PageHeader
          title="Historial"
          helpText="Registro de cambios y acciones realizadas en la plataforma."
        />
      </div>
      <div className="p-4">
        <Space
          className="historial-filters"
          wrap
          size="middle"
          style={{ marginBottom: 12 }}
        >
          <Input.Search
            allowClear
            placeholder="Buscar en resumen, detalle o quién"
            value={textoBusqueda}
            onChange={(e) => setTextoBusqueda(e.target.value)}
            onSearch={(v) => setQ(v || undefined)}
            style={{ width: 320 }}
          />
          <Select
            allowClear
            placeholder="Acción"
            value={accion}
            onChange={(v) => setAccion(v)}
            options={OPCIONES_ACCION}
            style={{ width: 190 }}
          />
          <RangePicker
            value={range}
            onChange={(vals) => {
              if (vals && vals[0] && vals[1]) {
                setRange([vals[0], vals[1]]);
              } else {
                setRange(null);
              }
            }}
          />
          <Button
            onClick={() => {
              setQ(undefined);
              setAccion(undefined);
              setUndone(undefined);
              setReversible(undefined);
              setRange(null);
            }}
          >
            Limpiar
          </Button>
        </Space>

        <Table<RecordRow>
          className="historial-table"
          rowKey="id"
          loading={loading}
          dataSource={data}
          columns={columns}
          pagination={{
            current: page,
            pageSize: size,
            total,
            showSizeChanger: true,
            onChange: (p, s) => fetchData(p, s),
          }}
          scroll={{ x: 1100 }}
          size="middle"
        />

        <Modal
          open={jsonViewer.open}
          onCancel={closeJson}
          title={jsonViewer.title || "Detalle JSON"}
          width={800}
          footer={<Button onClick={closeJson}>Cerrar</Button>}
          destroyOnClose
        >
          <Paragraph
            type="secondary"
            copyable={{ text: jsonText }}
            style={{ marginBottom: 8 }}
          >
            Copiar JSON
          </Paragraph>

          <pre
            className="hist-json-pre"
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              fontSize: 12,
              padding: 12,
              background: "#f7f7f7",
              borderRadius: 8,
              maxHeight: "60vh",
              overflow: "auto",
            }}
          >
            {jsonText}
          </pre>
        </Modal>
      </div>
    </Layout>
  );
};

export default Historial;
